/* The comment sheet as data.
 *
 * Everything the sheet is — the P3-09 header block in the order the form
 * prints it, the numbered rows, the findings that went to an SME instead, and
 * the pipe-delimited text — is here rather than in the page, so that the four
 * exports cannot drift from each other or from what is on screen. The .docx
 * writer, the .xlsx writer, "Copy as text" and the printed page all render this
 * one structure.
 *
 * It is also what makes the exports assertable. There is no browser test runner
 * in this repository (docs/redesign/ARCHITECTURE.md records why), so the column
 * order, the filtering and the text form are pure functions asserted directly
 * by tools/comment-sheet-check.tsx.
 *
 * Source of truth for every field and column: docs/redesign/P3-09.md.
 */

import {
  COMMENT_COLUMNS,
  commentFor,
  locationChip,
  submittalLabel,
} from '../data/review';
import type { CommentRow } from '../data/review';
import type { Flag, HeaderFields, ReviewRecord } from '../types';

export { COMMENT_COLUMNS };
export type { CommentRow };

/* ==========================================================================
   The header block
   ========================================================================== */

/**
 * One box on the form. `filled` is false where the reviewer has not supplied a
 * value — which is never an error and never blocks an export. The form has the
 * box whether or not it has been filled in, so the export prints the box empty,
 * exactly as a reviewer filling the template by hand would leave it.
 */
export interface HeaderEntry {
  /** The field's name as the form prints it. */
  label: string;
  value: string;
  filled: boolean;
  /** Which field this is, for the input the sheet renders against it. */
  field: keyof HeaderFields;
  /** 'date' and 'select' get the input the value deserves. */
  input: 'text' | 'date' | 'tel' | 'email' | 'select';
}

/**
 * The header block, in the order P3-09 prints it. "Other (ID)" only appears
 * when it is the submittal type in force; on any other type the form has no
 * box for it and neither does this.
 */
export function headerEntries(fields: HeaderFields): HeaderEntry[] {
  const entry = (
    label: string,
    field: keyof HeaderFields,
    input: HeaderEntry['input'] = 'text',
    value: string = String(fields[field] ?? ''),
  ): HeaderEntry => ({ label, field, input, value, filled: value.trim() !== '' });

  return [
    entry('Project Name/No.', 'projectName'),
    entry('Contract No.', 'contractNo'),
    entry('Date of Document', 'documentDate', 'date'),
    entry('Comments Due By', 'commentsDueBy', 'date'),
    entry(
      'Type of Submittal/Deliverable',
      'submittalType',
      'select',
      submittalLabel(fields.submittalType),
    ),
    ...(fields.submittalType === 'other'
      ? [entry('Other (ID)', 'submittalOther')]
      : []),
    entry('Reviewer', 'reviewerName'),
    entry('Date', 'reviewerDate', 'date'),
    entry('Phone', 'reviewerPhone', 'tel'),
    entry('Email', 'reviewerEmail', 'email'),
  ];
}

/**
 * "Four header fields are still empty." — the quiet amber note above the sheet.
 * Null when the block is complete. The submittal type always has a value, so it
 * can never be one of these.
 */
export function unfilledHeaderNote(entries: HeaderEntry[]): string | null {
  const missing = entries.filter((e) => !e.filled);
  if (missing.length === 0) return null;
  return missing.length === 1
    ? `${missing[0].label} is still empty. It will export blank.`
    : `${missing.length} header fields are still empty. They will export blank.`;
}

/* ==========================================================================
   Referred to an SME
   --------------------------------------------------------------------------
   A finding the reviewer handed to someone else is not a contractor comment,
   and it must not arrive on the contractor's desk as one. It is listed below
   the table, under its own heading, and it is not numbered into the form.
   ========================================================================== */

export interface ReferralRow {
  findingId: string;
  location: string;
  /** The plain-language claim, which is what an SME is being asked about. */
  claim: string;
  /** Who it went to. Empty where the reviewer did not name anyone. */
  referredTo: string;
  /** The reviewer's own note, where they left one. */
  note: string;
}

export function referralRows(
  findings: Flag[],
  review: ReviewRecord | null,
): ReferralRow[] {
  const dispositions = review?.dispositions ?? {};
  return findings
    .filter((f) => dispositions[f.id]?.decision === 'refer')
    .map((finding) => ({
      findingId: finding.id,
      location: locationChip(finding.sectionRef, finding.page) ?? '',
      claim: finding.claim,
      referredTo: dispositions[finding.id].referredTo?.trim() ?? '',
      note: dispositions[finding.id].note?.trim() ?? '',
    }));
}

/* ==========================================================================
   The sheet
   ========================================================================== */

export interface CommentSheetData {
  /** The review this came from, for the "Show the finding" links. */
  docId: string;
  /** What the sheet calls the document under review. */
  docName: string;
  header: HeaderEntry[];
  columns: readonly string[];
  rows: CommentRow[];
  referrals: ReferralRow[];
}

/** The title the exports carry. The form's own name, not a product name. */
export const SHEET_TITLE = 'ESPC Project Review Comment Template';

/**
 * The rows of the sheet: the findings the reviewer decided to include, in the
 * order the findings are in, numbered from one.
 *
 * Nothing else reaches the table. A finding that was set aside is absent, and a
 * finding that went to an SME is in `referrals` instead — a comment sheet is
 * what the reviewer owes the contractor, and the contractor is owed only what
 * the reviewer decided to send them.
 */
export function commentSheetData(
  docId: string,
  docName: string,
  findings: Flag[],
  review: ReviewRecord | null,
  headerFields: HeaderFields,
): CommentSheetData {
  const dispositions = review?.dispositions ?? {};
  const reviewer = headerFields.reviewerName.trim();

  const rows: CommentRow[] = findings
    .filter((f) => dispositions[f.id]?.decision === 'include')
    .map((finding, index) => ({
      findingId: finding.id,
      no: index + 1,
      location: locationChip(finding.sectionRef, finding.page) ?? '',
      comment: commentFor(
        { editedComments: review?.editedComments ?? {} },
        finding.id,
        finding.commentDraft,
      ),
      reviewer,
    }));

  return {
    docId,
    docName,
    header: headerEntries(headerFields),
    columns: COMMENT_COLUMNS,
    rows,
    referrals: referralRows(findings, review),
  };
}

/**
 * A comment is prose and may run to several sentences and several lines. The
 * delimiter has to survive that, so a cell is flattened to one line and any
 * pipe inside it becomes a slash.
 */
function flattenCell(value: string): string {
  return value.replace(/\s+/g, ' ').trim().replace(/\|/g, '/');
}

/**
 * The whole sheet as pipe-delimited text.
 *
 * This is the export that has no dependency and therefore cannot fail, which is
 * why it exists alongside the file writers rather than instead of them. It is
 * shaped to paste into the real .docx template: the header block is one
 * label/value pair per line, then a blank line, then the table — column names
 * first — so the table alone can be selected and run through Word's Convert
 * Text to Table on the pipe character. The referral list, when there is one,
 * follows a second blank line, so it is never pasted into the form by accident.
 */
export function commentSheetText(data: CommentSheetData): string {
  const line = (cells: readonly string[]) => cells.join(' | ');
  const blocks: string[] = [];

  blocks.push(
    [
      line([SHEET_TITLE, data.docName]),
      ...data.header.map((entry) => line([entry.label, entry.value])),
    ].join('\n'),
  );

  blocks.push(
    [
      line(data.columns),
      ...data.rows.map((row) =>
        line([
          String(row.no),
          row.location,
          flattenCell(row.comment),
          row.reviewer,
          // The other side's columns. Empty on purpose: their presence is what
          // makes this recognisable as the real form.
          '',
          '',
        ]),
      ),
    ].join('\n'),
  );

  if (data.referrals.length > 0) {
    blocks.push(
      [
        line(['Referred to an SME', 'Not contractor comments']),
        line(['Location', 'Finding', 'Referred to', 'Note']),
        ...data.referrals.map((row) =>
          line([
            row.location,
            flattenCell(row.claim),
            flattenCell(row.referredTo),
            flattenCell(row.note),
          ]),
        ),
      ].join('\n'),
    );
  }

  return blocks.join('\n\n');
}
